/**
 * Lifestyle & Habits - Educational information about everyday practices
 * commonly explored alongside nutrition for general wellbeing
 */

export const lifestyleHabits = [
  // MOVEMENT & PHYSICAL ACTIVITY
  {
    id: 'gentle-walking',
    name: 'Gentle Walking',
    category: 'Movement & Physical Activity',
    whyPeopleExplore: 'Often discussed in general wellbeing literature. Some people find walking to be an accessible form of movement.',
    commonApproaches: [
      'Short walks around the neighborhood',
      'Walking at a comfortable pace',
      'Indoor walking when weather is difficult',
      'Walking with others for social connection',
      'Taking breaks to walk during the day'
    ],
    generalNotes: 'Individual tolerance and preference varies. What feels manageable differs between people. Some people prefer shorter or gentler approaches depending on energy levels and circumstances.'
  },
  {
    id: 'stretching-mobility',
    name: 'Stretching & Mobility',
    category: 'Movement & Physical Activity',
    whyPeopleExplore: 'Commonly explored for maintaining flexibility and comfort in daily activities.',
    commonApproaches: [
      'Gentle stretching in the morning or evening',
      'Simple neck and shoulder movements',
      'Seated stretches',
      'Light yoga-style movements',
      'Range-of-motion exercises'
    ],
    generalNotes: 'Individual tolerance and preference varies. Some people prefer very gentle approaches. What feels comfortable differs between people and may change day to day.'
  },
  {
    id: 'balancing-activity-rest',
    name: 'Balancing Activity with Rest',
    category: 'Movement & Physical Activity',
    whyPeopleExplore: 'Often discussed for overall quality of life. Some people explore different approaches to managing energy throughout the day.',
    commonApproaches: [
      'Taking rest breaks between activities',
      'Pacing activities throughout the day',
      'Listening to body signals',
      'Adjusting plans based on energy levels',
      'Alternating between activity and rest'
    ],
    generalNotes: 'Individual needs vary greatly. What works for one person may not work for another. Energy levels can fluctuate, and approaches may need to be adjusted over time.'
  },

  // SLEEP & REST
  {
    id: 'sleep-consistency',
    name: 'Consistent Sleep Patterns',
    category: 'Sleep & Rest',
    whyPeopleExplore: 'Commonly discussed in general wellbeing contexts. Some people explore regular sleep timing as part of their daily routine.',
    commonApproaches: [
      'Going to bed at similar times',
      'Waking at similar times',
      'Creating bedtime routines',
      'Limiting daytime naps if they affect nighttime sleep',
      'Adjusting patterns gradually'
    ],
    generalNotes: 'Many public health sources commonly reference 7–9 hours of sleep for adults as a general guideline. Sleep needs vary between individuals. What feels restorative differs from person to person.'
  },
  {
    id: 'sleep-environment',
    name: 'Sleep Environment',
    category: 'Sleep & Rest',
    whyPeopleExplore: 'Often discussed for overall quality of life. Some people explore environmental adjustments to support their sleep.',
    commonApproaches: [
      'Reducing light in the bedroom',
      'Managing noise levels',
      'Adjusting room temperature',
      'Comfortable bedding',
      'Limiting screen use before bed'
    ],
    generalNotes: 'Individual preference varies widely. What one person finds comfortable may differ from another. Environmental factors can be adjusted gradually to find what works best.'
  },
  {
    id: 'rest-recovery',
    name: 'Rest & Recovery',
    category: 'Sleep & Rest',
    whyPeopleExplore: 'Commonly explored for daily balance. Some people incorporate various rest practices beyond nighttime sleep.',
    commonApproaches: [
      'Short rest periods during the day',
      'Quiet time without screens',
      'Lying down when needed',
      'Restful activities like reading or listening to music',
      'Allowing time for recovery after activities'
    ],
    generalNotes: 'Individual needs and preferences vary. Some people find brief rest periods helpful, while others prefer different approaches. What feels restorative differs between people.'
  },

  // MINDSET, STRESS & MENTAL WELLBEING
  {
    id: 'positive-thinking',
    name: 'Positive Thinking & Reframing',
    category: 'Mindset, Stress & Mental Wellbeing',
    whyPeopleExplore: 'Often discussed in general wellbeing literature. Some people explore different ways of approaching thoughts and perspectives.',
    commonApproaches: [
      'Noticing thought patterns',
      'Gratitude practices',
      'Journaling',
      'Positive affirmations',
      'Reframing challenges',
      'Reflecting on positive experiences'
    ],
    generalNotes: 'Individual experiences vary widely. What feels helpful to one person may differ from another. These are practices people explore, not prescribed approaches.'
  },
  {
    id: 'mindfulness-practices',
    name: 'Mindfulness & Present-Moment Awareness',
    category: 'Mindset, Stress & Mental Wellbeing',
    whyPeopleExplore: 'Commonly discussed in general wellbeing contexts. Some people explore present-moment awareness practices as part of their daily routine.',
    commonApproaches: [
      'Paying attention to the present moment',
      'Noticing sensations, thoughts, and feelings without judgment',
      'Brief mindfulness exercises',
      'Mindful eating or walking',
      'Body scan practices'
    ],
    generalNotes: 'Individual preference and experience varies. Some people find these practices accessible, while others prefer different approaches. What feels comfortable differs between people.'
  },
  {
    id: 'meditation',
    name: 'Meditation Practices',
    category: 'Mindset, Stress & Mental Wellbeing',
    whyPeopleExplore: 'Often discussed in general wellbeing literature. Some people explore meditation as a secular practice for daily life.',
    commonApproaches: [
      'Seated meditation',
      'Guided meditation recordings',
      'Brief meditation periods',
      'Walking meditation',
      'Loving-kindness practices',
      'Body awareness meditation'
    ],
    generalNotes: 'Individual tolerance and preference varies widely. Some people prefer very short sessions, while others practice longer periods. What feels manageable differs between people.'
  },
  {
    id: 'breathing-exercises',
    name: 'Breathing Exercises',
    category: 'Mindset, Stress & Mental Wellbeing',
    whyPeopleExplore: 'Commonly discussed in general wellbeing contexts. Some people explore different breathing patterns as part of their daily routine.',
    commonApproaches: [
      'Deep breathing',
      'Slow, paced breathing',
      'Diaphragmatic breathing',
      'Box breathing (equal counts)',
      'Natural breath observation'
    ],
    generalNotes: 'Individual tolerance varies. Some breathing patterns may not feel comfortable for everyone. What feels natural differs between people. These are practices people explore, not prescribed techniques.'
  },
  {
    id: 'social-connection',
    name: 'Social Connection',
    category: 'Mindset, Stress & Mental Wellbeing',
    whyPeopleExplore: 'Often discussed for overall quality of life. Some people explore different ways of maintaining connections with others.',
    commonApproaches: [
      'Regular contact with friends or family',
      'Joining groups or communities',
      'Online connections',
      'Sharing experiences with others',
      'Participating in social activities when energy allows'
    ],
    generalNotes: 'Individual needs and preferences vary widely. Some people prefer frequent social interaction, while others prefer quieter approaches. What feels supportive differs from person to person.'
  },
  {
    id: 'time-outdoors',
    name: 'Time Outdoors & Nature',
    category: 'Mindset, Stress & Mental Wellbeing',
    whyPeopleExplore: 'Commonly explored for general wellbeing. Some people find spending time in outdoor environments to be part of their daily routine.',
    commonApproaches: [
      'Short walks outside',
      'Sitting in a garden or park',
      'Opening windows for fresh air',
      'Observing nature',
      'Outdoor activities when weather permits'
    ],
    generalNotes: 'Individual circumstances and preferences vary. Access to outdoor spaces differs between people. What feels manageable or comfortable varies based on location, weather, and energy levels.'
  }
];

export const lifestyleCategories = [
  'Movement & Physical Activity',
  'Sleep & Rest',
  'Mindset, Stress & Mental Wellbeing'
];
